// components/SalarySlips.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Stack,
  alpha,
  useTheme,
  Badge,
  ButtonGroup,
  Popover,
  Checkbox,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CachedIcon from "@mui/icons-material/Cached";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ApiEndpoints from "../../network/ApiEndPoints";
import { get } from "../../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import GenerateSalarySlip from "./GenerateSalarySlip";

import ApiPaginateSearch from "../../component/ApiPaginateSearch";
import { CustomStyles } from "../../component/CustomStyle";
import SalarySlipViewModal from "./SalarySlipViewModal";

// Styled components (keep your existing styled components)
const StatCard = styled(Card)(({ theme, color }) => ({
  borderRadius: 12,
  background: `linear-gradient(135deg, ${color || "#1e3a5f"} 0%, ${color || "#2E5A88"} 100%)`,
  color: "#FFFFFF",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "150px",
    height: "150px",
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
    transform: "translate(50%, -50%)",
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, #1e3a5f 0%, #2E5A88 100%)`,
  color: "#FFFFFF",
  borderRadius: 8,
  padding: "8px 20px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(30, 58, 95, 0.2)",
  "&:hover": {
    background: `linear-gradient(135deg, #2E5A88 0%, #1e3a5f 100%)`,
  },
}));

const StatusChip = styled(Chip)(({ status }) => ({
  borderRadius: 8,
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 24,
  ...(status === "generated" && {
    backgroundColor: alpha("#2A9D8F", 0.1),
    color: "#2A9D8F",
  }),
  ...(status === "pending" && {
    backgroundColor: alpha("#E9C46A", 0.1),
    color: "#E9C46A",
  }),
  ...(status === "failed" && {
    backgroundColor: alpha("#E63946", 0.1),
    color: "#E63946",
  }),
}));

const companyColors = {
  primary: "#1e3a5f",
  secondary: "#2E5A88",
  accent: "#E63946",
  success: "#2A9D8F",
  warning: "#E9C46A",
  background: "#F8FAFC",
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
    disabled: "#94A3B8",
  },
  border: "#E2E8F0",
};

const SalarySlips = () => {
  const [slips, setSlips] = useState([]);
  const [query, setQuery] = useState("");
  const [stats, setStats] = useState({
    totalSlips: 0,
    totalNetSalary: 0,
    averageSalary: 0,
    thisMonthTotal: 0,
  });
  const [filters, setFilters] = useState({
    user_id: "",
    month: "",
    year: "",
  });
  const [employees, setEmployees] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortBy, setSortBy] = useState("date_desc");

  let refreshTable;

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  const fetchEmployees = async () => {
    get(
      `${ApiEndpoints.GET_USERS_BIGG}`,
      { is_employee: 1 },
      () => {},
      (res) => {
        setEmployees(res.data.data || []);
      },
      (error) => {
        console.error("Failed to fetch employees:", error);
      },
    );
  };

  useEffect(() => {
    fetchEmployees();
  }, []);



  const handleViewSlip = (slip) => {
    // Enhance slip data with necessary details for the view modal
    const enhancedSlip = {
      ...slip,
      earnings_breakdown: slip.earnings_breakdown || generateEarningsBreakdown(slip),
      deductions_breakdown: slip.deductions_breakdown || generateDeductionsBreakdown(slip),
      employee: slip.employee || {
        name: slip.employee_name,
        emp_id: `EMP${slip.user_id}`,
        email: slip.employee_email,
        department: slip.department || "Information Technology",
        role: slip.role || "Software Developer",
      },
      created_at: slip.created_at || new Date().toISOString(),
    };
    
    setSelectedSlip(enhancedSlip);
    setViewModal(true);
  };

  // Helper function to generate earnings breakdown if not provided by API
  const generateEarningsBreakdown = (slip) => {
    // This is a fallback - ideally your API should provide this data
    return [
      {
        component_name: "Basic Salary",
        amount: Math.round(slip.gross_salary * 0.6),
        calculation_type: "fixed",
      },
      {
        component_name: "HRA",
        amount: Math.round(slip.gross_salary * 0.3),
        calculation_type: "percentage",
        value: 30,
      },
      {
        component_name: "Conveyance",
        amount: Math.round(slip.gross_salary * 0.1),
        calculation_type: "percentage",
        value: 10,
      },
    ];
  };

  
  const generateDeductionsBreakdown = (slip) => {
    // This is a fallback - ideally your API should provide this data
    const pfAmount = Math.round(slip.gross_salary * 0.12);
    return [
      {
        component_name: "Provident Fund",
        amount: pfAmount,
        calculation_type: "percentage",
        value: 12,
      },
      {
        component_name: "Professional Tax",
        amount: 200,
        calculation_type: "fixed",
      },
      {
        component_name: "TDS",
        amount: slip.total_deduction - pfAmount - 200,
        calculation_type: "fixed",
      },
    ];
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (monthNumber) => {
    const month = months.find((m) => m.value === parseInt(monthNumber));
    return month ? month.label : "Unknown";
  };

  const filterOpen = Boolean(filterAnchorEl);

  // Column definitions for ApiPaginateSearch
  const columns = [
    {
      name: "Employee",
      selector: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(companyColors.primary, 0.1),
              color: companyColors.primary,
              fontWeight: 600,
            }}
          >
            {row.employee?.name?.charAt(0) ||
              row.employee_name?.charAt(0) ||
              "U"}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.employee?.name || row.employee_name || "N/A"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: companyColors.text.secondary }}
            >
              ID: {row.user_id}
            </Typography>
          </Box>
        </Box>
      ),
      sortable: true,
      sortField: "employee_name",
      grow: 2,
    },
    {
      name: "Period",
      selector: (row) => (
        <Chip
          label={`${row.month_name || getMonthName(row.month)} ${row.year}`}
          size="small"
          sx={{
            backgroundColor: alpha(companyColors.primary, 0.1),
            color: companyColors.primary,
            fontWeight: 500,
            borderRadius: "6px",
          }}
        />
      ),
      sortable: true,
      sortField: "year,month",
    },
    {
      name: "Gross Salary",
      selector: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatCurrency(row.gross_salary)}
        </Typography>
      ),
      sortable: true,
      sortField: "gross_salary",
      right: true,
    },
    {
      name: "Deductions",
      selector: (row) => (
        <Typography
          variant="body2"
          sx={{ color: companyColors.accent, fontWeight: 500 }}
        >
          {formatCurrency(row.total_deduction)}
        </Typography>
      ),
      sortable: true,
      sortField: "total_deduction",
      right: true,
    },
    {
      name: "Net Salary",
      selector: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: companyColors.success }}
        >
          {formatCurrency(row.net_salary)}
        </Typography>
      ),
      sortable: true,
      sortField: "net_salary",
      right: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <StatusChip 
          label={row.status || "Generated"} 
          status={row.status || "generated"} 
          size="small" 
        />
      ),
      center: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <ButtonGroup variant="text" size="small">
          <Tooltip title="View Details" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewSlip(row);
              }}
              sx={{ color: companyColors.primary }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
       
        </ButtonGroup>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: companyColors.background,
        minHeight: "100vh",
      }}
    >
   

      {/* Filters and Actions */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <GradientButton
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setGenerateModal(true)}
          >
            Generate Slip
          </GradientButton>
          
         
        </Box>

   
      </Box>

      {/* Main Table using ApiPaginateSearch */}
      <ApiPaginateSearch
        apiEnd={ApiEndpoints.GET_SALARY_SLIPS}
        columns={columns}
        pagename="Salary Slips"
        apiData={slips}
        setApiData={setSlips}
        refresh={() => refreshTable?.()}
        tableStyle={CustomStyles}
        queryParam={query}
        setQuery={setQuery}
        query={query}
        returnRefetch={(ref) => {
          refreshTable = ref;
        }}
        selectableRows={true}
       
      />

      {/* View Salary Slip Modal */}
      <SalarySlipViewModal
        open={viewModal}
        onClose={() => {
          setViewModal(false);
          setSelectedSlip(null);
        }}
        slip={selectedSlip}
      
        formatCurrency={formatCurrency}
        getMonthName={getMonthName}
      />

      {/* Generate Salary Slip Modal */}
      <GenerateSalarySlip
        open={generateModal}
        onClose={() => setGenerateModal(false)}
        onSuccess={() => {
          refreshTable?.();
          setGenerateModal(false);
        }}
      />
    </Box>
  );
};

export default SalarySlips;